import { useCallback, useEffect, useState } from 'react'
import { getSummaryReactions } from 'src/client/publication'
import { t } from 'src/i18n'
import { useUser } from 'src/hook/useUser'
import {
  UsePostSummaryReactionsParams,
  SummaryReactionsType,
  SummaryReactionsFetchStatus,
  PendingAction
} from './usePostSummaryReactions.types'

const usePostSummaryReactions = ({ postId, canFetch = true }: UsePostSummaryReactionsParams) => {
  const { user } = useUser()
  const [summaryReactions, setSummaryReactions] = useState<SummaryReactionsType>({})
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [fetchStatus, setFetchStatus] = useState<SummaryReactionsFetchStatus>(
    SummaryReactionsFetchStatus.IDLE
  )

  const fetchSummaryReactions = useCallback(async () => {
    if (!canFetch || fetchStatus !== SummaryReactionsFetchStatus.IDLE) return

    try {
      setFetchStatus(SummaryReactionsFetchStatus.LOADING)
      const response = await getSummaryReactions(postId)

      setSummaryReactions(response.reactions)
      setFetchStatus(SummaryReactionsFetchStatus.SUCCESS)

      return response
    } catch (error) {
      console.error('Error fetching summary reactions:', error)
      setFetchStatus(SummaryReactionsFetchStatus.Error)
      return undefined
    }
  }, [canFetch, fetchStatus, postId])

  const isReactionHasUserNames = useCallback(
    (reactionCode: string) => {
      if (!summaryReactions.hasOwnProperty(reactionCode)) return false
      const totalNames = summaryReactions[reactionCode].latestBy.length
      const remaining = summaryReactions[reactionCode]?.remaining || 0
      return totalNames > 0 || remaining > 0
    },
    [summaryReactions]
  )

  const extractUserNameFromReaction = useCallback(
    (reactionCode: string) => {
      if (!isReactionHasUserNames(reactionCode)) return ''

      const reaction = summaryReactions[reactionCode]
      const latestBy = reaction.latestBy || []
      const totalNames = latestBy.length
      const remaining = reaction.remaining || 0

      const names = latestBy.map(({ name, me }) => (me ? t('reaction.summary_names.you') : name))

      if (totalNames === 0 && remaining >= 1) {
        return t('reaction.summary_names.multiple_reacted_remaining', { remaining })
      }

      if (remaining === 1) {
        if (totalNames === 1) {
          return `${names[0]} ${t('reaction.summary_names.and')} ${t('reaction.summary_names.only_reacted_remaining')}`
        }
        return `${names.join(', ')} ${t('reaction.summary_names.and')} ${t('reaction.summary_names.only_reacted_remaining')}`
      }

      if (remaining > 1) {
        if (totalNames === 1) {
          return `${names[0]} ${t('reaction.summary_names.and')} ${t('reaction.summary_names.multiple_reacted_remaining', { remaining })}`
        }
        return `${names.join(', ')} ${t('reaction.summary_names.and')} ${t('reaction.summary_names.multiple_reacted_remaining', { remaining })}`
      }

      if (totalNames === 1) {
        return `${names[0]} ${t('reaction.summary_names.reacted')}`
      }

      if (totalNames === 2) {
        return `${names.join(` ${t('reaction.summary_names.and')} `)} ${t('reaction.summary_names.multiple_reactions')}`
      }

      const allButLast = names.slice(0, -1).join(', ')
      const last = names[totalNames - 1]
      return `${allButLast} ${t('reaction.summary_names.and')} ${last} ${t('reaction.summary_names.multiple_reactions')}`
    },
    [isReactionHasUserNames, summaryReactions]
  )

  const addMyUserNameToReaction = useCallback(
    (reactionCode: string) => {
      if (canFetch && fetchStatus !== SummaryReactionsFetchStatus.SUCCESS) {
        setPendingAction({ type: 'add', reactionCode })
        return
      }
      setSummaryReactions((oldData) => {
        const newValue = { name: user.name, me: true }

        if (!isReactionHasUserNames(reactionCode))
          return { ...oldData, [reactionCode]: { latestBy: [newValue] } }

        const old = oldData[reactionCode]
        const hasMe = old.latestBy.some(({ me }) => me)
        if (hasMe) return oldData

        const newLatestBy = [newValue, ...old.latestBy]
        return { ...oldData, [reactionCode]: { ...old, latestBy: newLatestBy } }
      })
    },
    [canFetch, fetchStatus, isReactionHasUserNames, user.name]
  )

  const removeMyUserNameToReaction = useCallback(
    (reactionCode: string) => {
      if (canFetch && fetchStatus !== SummaryReactionsFetchStatus.SUCCESS) {
        setPendingAction({ type: 'remove', reactionCode })
        return
      }

      setSummaryReactions((oldData) => {
        if (!oldData) return oldData

        const old = oldData[reactionCode]
        const hasMe = old?.latestBy.some(({ me }) => me)
        const remaining = old?.remaining || 0
        if (!hasMe && remaining > 0)
          return { ...oldData, [reactionCode]: { ...old, remaining: remaining - 1 } }

        const newLatestBy = old.latestBy.filter(({ me }) => !me)
        return { ...oldData, [reactionCode]: { ...old, latestBy: newLatestBy } }
      })
    },
    [canFetch, fetchStatus]
  )

  useEffect(() => {
    if (canFetch && fetchStatus === SummaryReactionsFetchStatus.SUCCESS && pendingAction) {
      if (pendingAction.type === 'add') {
        addMyUserNameToReaction(pendingAction.reactionCode)
      } else if (pendingAction.type === 'remove') {
        removeMyUserNameToReaction(pendingAction.reactionCode)
      }
      setPendingAction(null)
    }
  }, [fetchStatus, pendingAction, addMyUserNameToReaction, removeMyUserNameToReaction, canFetch])

  return {
    summaryReactions,
    fetchStatus,
    fetchSummaryReactions,
    isReactionHasUserNames,
    extractUserNameFromReaction,
    addMyUserNameToReaction,
    removeMyUserNameToReaction
  }
}

export default usePostSummaryReactions
